import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import Price from "../../common/Price";
import { PaymentMethodDiscount } from "../../../types/admin";

interface PaymentMethodDiscountViewModalProps {
  open: boolean;
  discount: PaymentMethodDiscount | null;
  loading?: boolean;
  onClose: () => void;
}

const PaymentMethodDiscountViewModal: React.FC<
  PaymentMethodDiscountViewModalProps
> = ({ open, discount, loading, onClose }) => {
  const { t } = useTranslation();
  const formatDiscountValue = (discount: PaymentMethodDiscount) => {
    if (discount.discountType === 1) {
      // Percentage
      return `${discount.discountValue}%`;
    } else {
      // FixedAmount
      return <Price value={discount.discountValue} disableCentsHeuristic />;
    }
  };

  const isExpired = (discount: PaymentMethodDiscount) => {
    if (!discount.endDate) return false;
    return new Date(discount.endDate) < new Date();
  };

  const hasReachedMaxUses = (discount: PaymentMethodDiscount) => {
    if (!discount.maxTotalUses) return false;
    return discount.currentTotalUses >= discount.maxTotalUses;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("admin.paymentDiscounts.discountDetails")}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : !discount ? (
          <Typography variant="body2" color="text.secondary">
            {t("admin.paymentDiscounts.noDiscountData")}
          </Typography>
        ) : (
          <Box sx={{ py: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6">
                    {discount.paymentMethodTypeDisplay}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Chip
                      label={discount.isActive ? t("admin.paymentDiscounts.active") : t("admin.paymentDiscounts.inactive")}
                      color={discount.isActive ? "success" : "default"}
                      size="small"
                    />
                    {isExpired(discount) && (
                      <Chip
                        label={t("admin.paymentDiscounts.expired")}
                        color="error"
                        size="small"
                      />
                    )}
                    {hasReachedMaxUses(discount) && (
                      <Chip
                        label={t("admin.paymentDiscounts.maxUsesReached")}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.description")}
                </Typography>
                <Typography variant="body1">{discount.description}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.paymentMethod")}
                </Typography>
                <Typography variant="body1">
                  {discount.paymentMethodTypeDisplay}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.discountType")}
                </Typography>
                <Typography variant="body1">
                  {discount.discountType === 1 ? t("admin.paymentDiscounts.percentage") : t("admin.paymentDiscounts.fixedAmount")}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.discountValue")}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatDiscountValue(discount)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.feeType")}
                </Typography>
                <Typography variant="body1">
                  {discount.feeTypeDisplay}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.minOrderValue")}
                </Typography>
                <Typography variant="body1">
                  {discount.minOrderValue ? (
                    <Price value={discount.minOrderValue} disableCentsHeuristic />
                  ) : (
                    t("admin.paymentDiscounts.noMinimum")
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.usage")}
                </Typography>
                <Typography variant="body1">
                  {discount.maxTotalUses
                    ? `${discount.currentTotalUses} / ${discount.maxTotalUses}`
                    : `${discount.currentTotalUses} (${t("admin.paymentDiscounts.unlimited")})`}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.startDate")}
                </Typography>
                <Typography variant="body1">
                  {discount.startDate
                    ? new Date(discount.startDate).toLocaleDateString()
                    : t("admin.paymentDiscounts.noStartDate")}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.endDate")}
                </Typography>
                <Typography variant="body1">
                  {discount.endDate
                    ? new Date(discount.endDate).toLocaleDateString()
                    : t("admin.paymentDiscounts.noEndDate")}
                </Typography>
              </Grid>

              {discount.notes && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("admin.paymentDiscounts.notes")}
                    </Typography>
                    <Typography variant="body1">{discount.notes}</Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.paymentDiscounts.createdAt")}
                </Typography>
                <Typography variant="body2">
                  {new Date(discount.createdAt).toLocaleString()}
                </Typography>
              </Grid>

              {discount.updatedAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("admin.paymentDiscounts.updatedAt")}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(discount.updatedAt).toLocaleString()}
                  </Typography>
                </Grid>
              )}

              {discount.createdBy && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("admin.paymentDiscounts.createdBy")}
                  </Typography>
                  <Typography variant="body2">{discount.createdBy}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodDiscountViewModal;

